import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import BASE_URL from "../utils";
import { sizedUploadUrl } from "../utils/imageUrl";

/* -------------------------------------------
   ✅ Auto-detect API URL
-------------------------------------------- */


export default function BlogPreviewSection() {
  const [blogs, setBlogs] = useState([]);
  const [filteredBlogs, setFilteredBlogs] = useState([]);
  const [activeTab, setActiveTab] = useState("All Posts");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${BASE_URL}/api/blogs`)
      .then((res) => {
        const allBlogs = res.data.blogs || [];

        // ✅ Sort by publish date (latest first)
        const sorted = [...allBlogs].sort((a, b) => {
          const dateA = new Date(a.publishDate || 0);
          const dateB = new Date(b.publishDate || 0);
          return dateB - dateA;
        });

        setBlogs(sorted);
        setFilteredBlogs(sorted.slice(0, 3));
      })
      .catch((err) => console.error("Error loading blogs:", err))
      .finally(() => setLoading(false));
  }, []);

  // ✅ Handle Tab Change
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === "All Posts") {
      setFilteredBlogs(blogs.slice(0, 3));
    } else if (tab === "Lifestyle") {
      const life = blogs
        .filter((b) => b.category?.toLowerCase().includes("lifestyle"))
        .slice(0, 3);
      setFilteredBlogs(life);
    } else if (tab === "Recipes") {
      const recipe = blogs
        .filter((b) => b.category?.toLowerCase().includes("recipe"))
        .slice(0, 3);
      setFilteredBlogs(recipe);
    }
  };

  const SkeletonCard = () => (
    <div className="border rounded-lg overflow-hidden animate-pulse bg-white">
      <div className="h-48 bg-gray-200" />
      <div className="p-4 space-y-2">
        <div className="h-4 bg-gray-200 w-24 rounded" />
        <div className="h-6 bg-gray-200 w-3/4 rounded" />
        <div className="h-4 bg-gray-200 w-full rounded" />
      </div>
    </div>
  );

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-6 text-center">
        {/* Header */}
        <h2 className="text-4xl sm:text-5xl font-semibold mb-4">
          The <span className="text-[#C03CA0] font-bold">Plant-Powered</span> Journal
        </h2>

        <p className="text-gray-600 max-w-2xl mx-auto mb-10">
          Introducing our exquisite selection of plant-based, dairy-free cheeses
          and products. Indulge in the deliciousness of our creations, made from
          the finest plant-based ingredients, and experience a dairy-free journey
          that surpasses all expectations.
        </p>

        {/* Tabs */}
        <div className="flex justify-center gap-4 mb-10">
          {["All Posts", "Lifestyle", "Recipes"].map((tab) => (
            <button
              key={tab}
              onClick={() => handleTabChange(tab)}
              className={`px-6 py-2 rounded-full font-semibold text-sm transition ${
                activeTab === tab
                  ? "bg-[#C03CA0] text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Blog Cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
          {loading
            ? Array(3)
                .fill(0)
                .map((_, i) => <SkeletonCard key={i} />)
            : filteredBlogs.length > 0
            ? filteredBlogs.map((b) => (
                <Link
                  key={b._id}
                  to={`/blog/${b.handle}`}
                  className="group block border rounded-lg overflow-hidden hover:shadow-lg transition bg-white cursor-pointer"
                >
                  {b.image && (
                    <img
                      src={sizedUploadUrl(b.image, 800)}
                      alt={b.title}
                      width={800}
                      height={416}
                      loading="lazy"
                      decoding="async"
                      className="h-52 w-full object-cover group-hover:scale-[1.03] transition-transform duration-300 will-change-transform"
                    />
                  )}
                  <div className="p-4 text-left">
                    <p className="text-xs text-gray-500 mb-1">
                      {b.publishDate
                        ? new Date(b.publishDate).toLocaleDateString("en-US", {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                          })
                        : ""}
                    </p>
                    <h2 className="text-lg font-semibold mb-2 line-clamp-2 group-hover:text-[#C03CA0] transition-colors">
                      {b.title}
                    </h2>
                    <span className="text-[#C03CA0] text-sm font-medium mt-3 inline-block group-hover:underline">
                      Read More →
                    </span>
                  </div>
                </Link>
              ))
            : (
              <p className="text-gray-500 col-span-full text-center">
                No posts found for this category.
              </p>
            )}
        </div>

        {/* View All Button */}
        <Link
          to="/blogs"
          className="inline-block bg-[#C03CA0] text-white px-8 py-3 rounded-full font-medium hover:bg-[#a82f87] transition"
        >
          View All
        </Link>
      </div>
    </section>
  );
}
